import {
  chosenReading,
  type LineInfo,
  lineSyllables,
  type Overrides,
  reachableTotals,
  type WordInfo,
} from "@/lib/tool-kit/poetry/pronounce";

/* --------------------------------------------------------------- patterns */

export type PatternId = "classic" | "short" | "custom";

export const PATTERNS: Readonly<
  Record<Exclude<PatternId, "custom">, number[]>
> = {
  classic: [5, 7, 5],
  short: [3, 5, 3],
};

const RE_PATTERN_SPLIT = /[^0-9]+/;
const MAX_LINES = 12;
const MAX_LINE_SYLLABLES = 40;

/** "5-7-5", "5, 7, 5, 7, 7" → [5, 7, 5, …]; null when nothing usable is typed. */
export function parsePattern(input: string): number[] | null {
  const numbers = input
    .split(RE_PATTERN_SPLIT)
    .filter((part) => part.length > 0)
    .map(Number)
    .filter((n) => n >= 1 && n <= MAX_LINE_SYLLABLES);
  if (numbers.length === 0 || numbers.length > MAX_LINES) {
    return null;
  }
  return numbers;
}

/* ----------------------------------------------------------------- checks */

export type LineStatus = "match" | "over" | "under" | "possible" | "extra";

export interface LineCheck {
  line: LineInfo;
  syllables: number;
  /** The syllables this line should have; null for a line beyond the pattern. */
  target: number | null;
  status: LineStatus;
  /** syllables − target; 0 when there is no target. */
  difference: number;
}

export type Verdict = "match" | "possible" | "off";

export interface HaikuCheck {
  lines: LineCheck[];
  /** Lines the pattern wants that the poem lacks. */
  missingLines: number;
  verdict: Verdict;
  total: number;
  targetTotal: number;
}

function statusOf(
  line: LineInfo,
  syllables: number,
  target: number | null
): LineStatus {
  if (target === null) {
    return "extra";
  }
  if (syllables === target) {
    return "match";
  }
  if (reachableTotals(line.words).has(target)) {
    return "possible";
  }
  return syllables > target ? "over" : "under";
}

/** Check a poem's lines against a syllable pattern, under the reader's word choices. */
export function checkPattern(
  lines: readonly LineInfo[],
  pattern: readonly number[],
  overrides: Overrides
): HaikuCheck {
  const checks: LineCheck[] = lines.map((line, index) => {
    const syllables = lineSyllables(line.words, overrides);
    const target = pattern[index] ?? null;
    return {
      line,
      syllables,
      target,
      status: statusOf(line, syllables, target),
      difference: target === null ? 0 : syllables - target,
    };
  });
  const missingLines = Math.max(0, pattern.length - lines.length);
  const allMatch =
    missingLines === 0 && checks.every((check) => check.status === "match");
  const reachable =
    missingLines === 0 &&
    checks.every(
      (check) => check.status === "match" || check.status === "possible"
    );
  let verdict: Verdict = "off";
  if (allMatch) {
    verdict = "match";
  } else if (reachable) {
    verdict = "possible";
  }
  return {
    lines: checks,
    missingLines,
    verdict,
    total: checks.reduce((sum, check) => sum + check.syllables, 0),
    targetTotal: pattern.reduce((sum, n) => sum + n, 0),
  };
}

/** Words in the poem a reader could read more than one way, once each. */
export function ambiguousWords(lines: readonly LineInfo[]): WordInfo[] {
  const seen = new Set<string>();
  const out: WordInfo[] = [];
  for (const line of lines) {
    for (const word of line.words) {
      if (word.alternatives.length > 0 && !seen.has(word.key)) {
        seen.add(word.key);
        out.push(word);
      }
    }
  }
  return out;
}

/** The readings of a word, default first, with the one in force marked. */
export function readingChoices(word: WordInfo, overrides: Overrides) {
  const current = chosenReading(word, overrides).syllables;
  return [word.reading, ...word.alternatives].map((reading) => ({
    reading,
    chosen: reading.syllables === current,
  }));
}

/** The next reading's syllable count when a word's chip is pressed. */
export function nextReading(word: WordInfo, overrides: Overrides): number {
  const all = [word.reading, ...word.alternatives];
  const current = chosenReading(word, overrides).syllables;
  const at = all.findIndex((reading) => reading.syllables === current);
  return all[(at + 1) % all.length].syllables;
}

/* ------------------------------------------------------------ season words */

export type Season = "spring" | "summer" | "autumn" | "winter";

export interface SeasonWord {
  word: string;
  season: Season;
}

/**
 * A sample of English words that are seasonal references (kigo) in the Japanese
 * saijiki tradition — not the whole list, and only the plain, well-attested ones. Note
 * the tradition's own seasons: the moon on its own is an autumn word, and frogs sing in
 * spring.
 */
const SEASON_WORDS: Readonly<Record<Season, string>> = {
  spring:
    "blossom blossoms thaw frog frogs swallow swallows skylark skylarks butterfly butterflies haze bud buds seedling seedlings tulip tulips violet violets daffodil daffodils spring wisteria",
  summer:
    "summer cicada cicadas firefly fireflies mosquito mosquitoes thunder cuckoo peony peonies sunflower sunflowers heat sultry lotus",
  autumn:
    "autumn moon dragonfly dragonflies scarecrow chrysanthemum chrysanthemums cricket crickets persimmon persimmons geese harvest typhoon dew maple milky chestnut chestnuts",
  winter:
    "winter snow snowflake snowflakes snowfall frost icicle icicles ice hail cold daikon",
};

const SEASON_BY_WORD: ReadonlyMap<string, Season> = new Map(
  (Object.keys(SEASON_WORDS) as Season[]).flatMap((season) =>
    SEASON_WORDS[season]
      .split(" ")
      .map((word): [string, Season] => [word, season])
  )
);

/** Seasonal words in a poem, once each, in reading order. */
export function findSeasonWords(lines: readonly LineInfo[]): SeasonWord[] {
  const found: SeasonWord[] = [];
  const seen = new Set<string>();
  for (const line of lines) {
    for (const word of line.words) {
      const season = SEASON_BY_WORD.get(word.key);
      if (season && !seen.has(word.key)) {
        seen.add(word.key);
        found.push({ word: word.key, season });
      }
    }
  }
  return found;
}
